<script>
    export let selectedItem; // Receive selectedItem as a prop
    import { onMount } from 'svelte';
    import { fetchEgresoDropFiles } from '../../controllers/egresoDropFilesController.js';
    import { fetchEgresoDrops } from '../../controllers/egresoDropController.js';
  
    let enlargedImage = null; // Track the currently enlarged image
    let isModalOpen = false; // Track whether the modal is open
    let egresoDrops = []; // Array to store drops
    let dropFiles = {}; // Object to store files by drop ID
    let isLoading = true; // Loading state
  
    const BASE_URL = 'https://pb-105921165.seraphsystems.com/api/files/pbc_373482567';
    let lastSelectedId = null;
  
    // Function to open the modal with the enlarged image
    function openModal(file) {
      enlargedImage = `${BASE_URL}/${file.id}/${file.file}`;
      isModalOpen = true;
    }
  
    // Function to close the modal
    function closeModal() {
      isModalOpen = false;
      enlargedImage = null;
    }
  
    // Load drops and their files
    async function loadDropsAndFiles() {
      if (!selectedItem?.id) return;
      
      try {
        isLoading = true;
        
        // Load drops for this egreso
        egresoDrops = await fetchEgresoDrops(selectedItem.id);
        
        // Load files for each drop
        for (const drop of egresoDrops) {
          try {
            const filesResponse = await fetchEgresoDropFiles(drop.id);
            dropFiles[drop.id] = filesResponse;
          } catch (error) {
            console.error(`Error loading files for drop ${drop.id}:`, error);
            dropFiles[drop.id] = [];
          }
        }
      } catch (error) {
        console.error("Error loading drops and files:", error);
        egresoDrops = [];
        dropFiles = {};
      } finally {
        isLoading = false;
      }
    }
  
    // Close modal when clicking outside the image
    onMount(() => {
      const handleClickOutside = (event) => {
        if (isModalOpen && event.target.classList.contains('modal-backdrop')) {
          closeModal();
        }
      };
      document.addEventListener('click', handleClickOutside);
      
      // Load data when component mounts
      loadDropsAndFiles();
      
      return () => document.removeEventListener('click', handleClickOutside);
    });
    
    // Reload data when selectedItem changes
    $: if (selectedItem?.id && selectedItem.id !== lastSelectedId) {
      lastSelectedId = selectedItem.id;
      loadDropsAndFiles();
    }
  </script>
  
  <div class="gallery-container">
    {#if isLoading}
      <div class="loading-message">
        <p>Cargando galerías...</p>
      </div>
    {:else if egresoDrops.length === 0}
      <p class="no-drops-message">No hay drops disponibles para mostrar galerías.</p>
    {:else}
      {#each egresoDrops as drop}
        <div class="drop-gallery">
          <h3 class="drop-title">Drop: {drop.target_ref || drop.delivery_ref || `Drop #${drop.id}`}</h3>
          
          {#if dropFiles[drop.id] && dropFiles[drop.id].length > 0}
            <div class="thumbnails-container">
              {#each dropFiles[drop.id] as file}
                <div class="thumbnail" on:click={() => openModal(file)}>
                  <img
                    src={`${BASE_URL}/${file.id}/${file.file}?thumb=100x100`}
                    alt={file.file}
                    class="thumbnail-image"
                  />
                  <p class="thumbnail-caption">{file.file}</p>
                </div>
              {/each}
            </div>
          {:else}
            <p class="no-files-message">No hay archivos disponibles para este drop.</p>
          {/if}
        </div>
      {/each}
    {/if}
  </div>
  
  {#if isModalOpen}
    <div class="modal-backdrop">
      <div class="modal-content">
        <button class="close-button" on:click={closeModal}>✕</button>
        <img src={enlargedImage} alt="Enlarged Image" class="enlarged-image" />
      </div>
    </div>
  {/if}
  
  <style>
    .gallery-container {
      padding: 10px;
    }
    
    .loading-message,
    .no-drops-message {
      text-align: center;
      padding: 20px;
      color: #666;
      font-style: italic;
    }
    
    .drop-gallery {
      margin-bottom: 30px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
    }
    
    .drop-title {
      background-color: #f3f4f6;
      padding: 12px 16px;
      margin: 0;
      font-size: 1.1em;
      font-weight: 600;
      color: #374151;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .thumbnails-container {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      padding: 16px;
    }
    
    .no-files-message {
      padding: 16px;
      color: #666;
      font-style: italic;
      text-align: center;
    }
  
    .thumbnail {
      width: 100px;
      text-align: center;
      cursor: pointer;
      transition: transform 0.2s ease;
    }
    
    .thumbnail:hover {
      transform: scale(1.05);
    }
  
    .thumbnail-image {
      width: 100%;
      height: auto;
      border-radius: 5px;
      border: 2px solid transparent;
      transition: border-color 0.2s ease;
    }
    
    .thumbnail:hover .thumbnail-image {
      border-color: #3b82f6;
    }
  
    .thumbnail-caption {
      margin-top: 5px;
      font-size: 0.8em;
      color: #666;
      word-break: break-word;
    }
  
    .modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }
  
    .modal-content {
      position: relative;
      background-color: white;
      padding: 20px;
      border-radius: 10px;
      max-width: 90%;
      max-height: 90%;
      overflow: auto;
    }
  
    .close-button {
      position: absolute;
      top: 10px;
      right: 10px;
      background: none;
      border: none;
      font-size: 1.5em;
      cursor: pointer;
      color: #333;
      padding: 5px;
      border-radius: 50%;
      transition: background-color 0.2s ease;
    }
    
    .close-button:hover {
      background-color: #f3f4f6;
    }
  
    .enlarged-image {
      max-width: 100%;
      max-height: 80vh;
      border-radius: 5px;
    }
  </style>